import { keyBy, find, cloneDeep, forEach } from '@dt/fe-utils/dist/lodash'
import { makeAutoObservable, runInAction } from 'mobx'
import { cartMobx, productMobx, RESTAURANT_EVENT, restaurantCenter, restaurantConfig, storeMobx } from '..'
import BoxFeeApi from 'api/boxPrice.api'

class BoxPriceMobx {
  constructor () {
    makeAutoObservable(this)

    /** 监听 税费的配置 变更 */
    restaurantCenter.on(RESTAURANT_EVENT.EAT_TYPE_CHANGE, ({ eatType }) => {
      this.setConfig(cartMobx.channel, eatType)
    })

    /** 监听 订单 channel 变更 */
    restaurantCenter.on(RESTAURANT_EVENT.ORDER_CHANNEL_CHANGED, ({ channel }) => {
      this.setConfig(channel, cartMobx.info.eatType)
    })
  }

  /** 当前门店打包费全规则 */
  allBoxFees: IStoreBoxFeeItem[] = []

  /** 当前打包费配置 */
  boxFeeConfig: IBoxFeeItem | undefined = undefined

  /** 打包费类型
   * productBoxFee 餐品级
   * ordersBoxFee 订单级
   */
  boxFeeType: TboxFeeType = 'productBoxFee'

  /** 渠道 */
  channel: TChannel = 'pos'

  /** 就餐类型 */
  eatType: TEatType = 'dinein'

  /** 已下单 */
  ordersBoxFee: number = 0

  /** 打包费 pk */
  get getBoxPk () {
    return `box_${this.boxFeeConfig?.boxFeeConfigId}`
  }

  /** 场景和就餐方式map索引 */
  get channelEatTypeToConfigMap () {
    return keyBy(this.allBoxFees, (item) => `${item.channel}${item.eatType}`)
  }

  /** 初始化 */
  async init () {
    const params = {
      brandId: storeMobx.info.brandId || restaurantConfig.brandId,
      storeId: storeMobx.info.storeId || restaurantConfig.storeId,
    }
    try {
      const res: any = await BoxFeeApi.getStoreOrdersBoxFee(params)
      if (res?.content) {
        runInAction(() => {
          this.allBoxFees = res.content || []
          this.setConfig(cartMobx.channel, cartMobx.info.eatType)
        })
      }
    } catch (error) {
    }
  }

  /** 判断是否有打包餐品 */
  checkTogo (products: any, defaultTogo: 0 | 1) {
    return find(products, (item) => {
      return item.isTogo ||
        find((item.swapBuyProducts || []), item => item?.isTogo) ||
        find((item.fullItemNFreeProducts || []), item => item?.isTogo)
    }) || defaultTogo
  }

  /** 获取当前channl 和eatType 下匹配的打包费方案 */
  setConfig (channel: TChannel, eatType: TEatType) {
    const _channel = restaurantConfig.codeType === 'brand' && ['pickup', 'delivery'].includes(eatType) ? 'selfDelivery' : channel
    const bfConfig = this.channelEatTypeToConfigMap[`${_channel}${eatType}`]
    if (bfConfig?.data && Object.keys(bfConfig.data).length) {
      this.boxFeeConfig = cloneDeep(bfConfig.data)
      this.boxFeeType = 'ordersBoxFee'
    } else {
      this.boxFeeType = 'productBoxFee'
    }
    this.channel = _channel
    this.eatType = eatType
  }

  /** 计算购物车餐品实际总价 + 加料规格，不包含优惠（包含换购,满送） */
  calcCartTotalPrice (cartItems: IViewCartItem[], isOrder?: boolean) {
    let totalPrice = 0
    forEach(cartItems, (item: IViewCartItem) => {
      if (!item.disabled) {
        totalPrice += item.totalPrice
        if (item.swapBuyProducts?.length) {
          totalPrice += this.calcCartTotalPrice(item.swapBuyProducts, isOrder)
        }
        if (item.fullItemNFreeProducts?.length) {
          totalPrice += this.calcCartTotalPrice(item.fullItemNFreeProducts, isOrder)
        }
      }
    })
    return totalPrice
  }

  /** 计算订单餐品实际总价 + 加料规格，不包含优惠（包含换购,满送） */
  calcOrdersTotalPrice (cartItems: IViewOrderDetail[]) {
    let totalPrice = 0
    forEach(cartItems, (item: IViewOrderDetail) => {
      if (!(item.refundCnt && item.refundCnt === item.originalCnt)) {
        totalPrice += item.totalPrice
      }
      if (item.swapBuyProducts?.length) {
        totalPrice += this.calcOrdersTotalPrice(item.swapBuyProducts)
      }
      if (item.fullItemNFreeProducts?.length) {
        totalPrice += this.calcOrdersTotalPrice(item.fullItemNFreeProducts)
      }
    })
    return totalPrice
  }

  /** 计算购物车打包费 */
  calcCartBoxFee (cartItems: IViewCartItem[], defaultTogo: 0 | 1) {
    const mapProducts = productMobx.noToViewProductMap
    let boxFee = 0
    forEach(cartItems, (item) => {
      // 未售罄
      if (!item.disabled) {
        const viewProduct = mapProducts[item.productNo] // 菜单
        const isTogo = item.isTogo || defaultTogo || 0
        const scale = (viewProduct && viewProduct.scales.find((s: any) => s.no === item.scaleNo)) || { togoFee: 0 }
        let groupsBoxPrice = 0
        if (item.productGroups?.length) {
          item.productGroups.forEach(group => {
            groupsBoxPrice += this.calcCartBoxFee(group.productGroupDetails, defaultTogo)
          })
        }
        const unitBoxPrice = (item.productGroups?.length ? (groupsBoxPrice || 0) : (scale.togoFee || 0))
        boxFee += isTogo ? unitBoxPrice * item.cnt : 0
        if (item.swapBuyProducts?.length) {
          boxFee += this.calcCartBoxFee(item.swapBuyProducts, defaultTogo)
        }
        if (item.fullItemNFreeProducts?.length) {
          boxFee += this.calcCartBoxFee(item.fullItemNFreeProducts, defaultTogo)
        }
      }
    })
    return boxFee
  }

  /** 计算已下单打包费 */
  calcOrdersBoxFee (ordersItems: IViewOrderDetail[]) {
    let boxFee = 0
    forEach(ordersItems, (item) => {
      if (!(item.refundCnt && item.refundCnt === item.originalCnt)) {
        boxFee += item.totalBoxPrice
      }
      if (item.swapBuyProducts?.length) {
        boxFee += this.calcOrdersBoxFee(item.swapBuyProducts)
      }
      if (item.fullItemNFreeProducts?.length) {
        boxFee += this.calcOrdersBoxFee(item.fullItemNFreeProducts)
      }
    })
    return boxFee
  }

  /** 计算打包费
   * @param cartItems 购物车餐品
   * @param orderItems 已下单餐品，后付费才有
   * @param orderPrice? 已下单查询打包费接口还没有，先自己算
   * @return fee
   */
  calcBoxFee ({ cartItems, ordersItems, boxFeeType, eatType }: { cartItems: IViewCartItem[]; ordersItems?: IViewOrderDetail[]; boxFeeType?: 'ordersBoxFee' | 'productBoxFee'; orderPrice?: number; eatType?: TEatType }) {
    // 当前默认 堂食默认不打包，其余就餐方式必须打包
    if (eatType && eatType !== this.eatType) {
      // 处理: 直接修改下单场景下就餐方式没走监听
      this.setConfig(this.channel, eatType)
    }
    const defaultTogo = this.eatType !== 'dinein' ? 1 : 0
    if (this.channel === '3rdDelivery') {
      // 2023/05/09 三方外卖不要打包费
      return 0
    }
    if (this.boxFeeType === 'ordersBoxFee' || boxFeeType === 'ordersBoxFee') { // ordersBoxFee
      // 是否有打包餐品
      const isTogo = this.checkTogo(cartItems || [], defaultTogo)
      const isTogoOrder = this.checkTogo(ordersItems || [], defaultTogo)
      if (!isTogo && !isTogoOrder) return 0
      const cartPrice = this.calcCartTotalPrice(cartItems || [])
      const orderPrice = this.calcOrdersTotalPrice(ordersItems || [])
      const totalPrice = cartPrice + orderPrice
      const feeConfiig = find(this.boxFeeConfig?.child, (item) => totalPrice >= item.condition.min && totalPrice <= item.condition.max) || this.boxFeeConfig
      return feeConfiig?.fee || 0
    } else if (this.boxFeeType === 'productBoxFee' || boxFeeType === 'productBoxFee') {
      const orderPrice = this.calcOrdersBoxFee(ordersItems || [])
      const cartPrice = this.calcCartBoxFee(cartItems, defaultTogo)
      return orderPrice + cartPrice
    }
    return 0
  }
}

export default new BoxPriceMobx()
